import type { ReactNode, SVGProps } from "react";
import { faBadge } from "../i18n/digits";
import { fa } from "../i18n/fa";
import type { View } from "../store/session";

type IconType = "games" | "friends" | "chat" | "profile" | "leaderboard";

/**
 * Inline SVG icons mirroring the Stitch design's Material Symbols
 * (casino / group / chat / person). We don't ship an icon font, so each glyph is
 * a self-contained `currentColor` SVG. Kept local to the nav to avoid colliding
 * with the shared `GameIcon` component.
 */
function NavIcon({ type }: { type: IconType }): ReactNode {
  const svg: SVGProps<SVGSVGElement> = {
    viewBox: "0 0 24 24",
    width: 24,
    height: 24,
    fill: "currentColor",
    "aria-hidden": true,
  };
  if (type === "games") {
    // casino: a die (outlined body) with five pips
    return (
      <svg {...svg} fill="none">
        <rect x="4" y="4" width="16" height="16" rx="3.5" stroke="currentColor" strokeWidth={1.8} />
        <g fill="currentColor">
          <circle cx="8.4" cy="8.4" r="1.25" />
          <circle cx="15.6" cy="8.4" r="1.25" />
          <circle cx="12" cy="12" r="1.25" />
          <circle cx="8.4" cy="15.6" r="1.25" />
          <circle cx="15.6" cy="15.6" r="1.25" />
        </g>
      </svg>
    );
  }
  if (type === "friends") {
    // group: two person silhouettes
    return (
      <svg {...svg}>
        <circle cx="9" cy="8.2" r="3.1" />
        <path d="M3.5 18.6c0-3 2.7-4.9 5.5-4.9s5.5 1.9 5.5 4.9v.7H3.5z" />
        <circle cx="16.7" cy="9" r="2.5" />
        <path d="M16.7 13.5c-.7 0-1.4.1-2 .3 1.1.9 1.8 2.2 1.8 3.8v.7h5.4v-.7c0-2.3-2.3-4.1-5.2-4.1z" />
      </svg>
    );
  }
  if (type === "leaderboard") {
    // trophy: a cup on a stepped plinth — the podium the screen itself is built around
    return (
      <svg {...svg} fill="none">
        <path
          d="M7 4h10v5a5 5 0 0 1-10 0V4z"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinejoin="round"
        />
        <path
          d="M7 5.5H4.6v1.2A3.2 3.2 0 0 0 7.4 9.9M17 5.5h2.4v1.2a3.2 3.2 0 0 1-2.8 3.2"
          stroke="currentColor"
          strokeWidth={1.6}
          strokeLinecap="round"
        />
        <path d="M12 14v3.2" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" />
        <path
          d="M8.2 20.2h7.6l-.7-2.4a.8.8 0 0 0-.77-.6H9.67a.8.8 0 0 0-.77.6l-.7 2.4z"
          fill="currentColor"
        />
      </svg>
    );
  }
  if (type === "chat") {
    // chat: a rounded speech bubble with a tail
    return (
      <svg {...svg}>
        <path d="M5 3.5h14c1.4 0 2.5 1.1 2.5 2.5v8c0 1.4-1.1 2.5-2.5 2.5h-7l-5 4v-4H5c-1.4 0-2.5-1.1-2.5-2.5V6C2.5 4.6 3.6 3.5 5 3.5z" />
      </svg>
    );
  }
  // person
  return (
    <svg {...svg}>
      <circle cx="12" cy="8" r="3.7" />
      <path d="M4.8 19.4c0-3.4 3.3-5.5 7.2-5.5s7.2 2.1 7.2 5.5v.6H4.8z" />
    </svg>
  );
}

const TABS: readonly { view: View; icon: IconType; label: string }[] = [
  { view: "lobby", icon: "games", label: fa.nav.games },
  { view: "friends", icon: "friends", label: fa.nav.friends },
  { view: "chat", icon: "chat", label: fa.nav.chat },
  // «برترین‌ها» sits between گفتگو and پروفایل — a destination, not a profile sub-page.
  { view: "leaderboard", icon: "leaderboard", label: fa.nav.leaderboard },
  { view: "profile", icon: "profile", label: fa.nav.profile },
];

/**
 * Shared bottom tab bar (Stitch "BottomNavBar"). Kept as a flex item (not
 * `fixed`) so it slots under the scrollable screen in `App`. Same
 * `{ active, onNavigate }` contract other screens already rely on.
 */
export function BottomNav({
  active,
  onNavigate,
  friendsBadge = 0,
  chatBadge = 0,
}: {
  active: View;
  onNavigate: (view: View) => void;
  /** Unread incoming friend-request count, badged on the Friends tab (0 = hidden). */
  friendsBadge?: number;
  /** Total unread DM count, badged on the چت tab (0 = hidden). */
  chatBadge?: number;
}): ReactNode {
  return (
    <nav
      aria-label={fa.appName}
      // Hidden while the on-screen keyboard is up (CSS, keyed off `:root[data-keyboard]`
      // — see theme.css). It is exactly what Android's keyboard toolbar (copy/paste/
      // emoji) was covering, and while you are typing it is dead weight that steals room
      // from the composer.
      data-bottom-nav
      className="flex shrink-0 items-center justify-around gap-1 w-full max-w-container-max mx-auto px-gutter pt-sm bg-surface-container border-t border-outline-variant/30 shadow-[0_-4px_12px_rgba(0,0,0,0.5)]"
      style={{
        // Clear the iOS home indicator. In Bot-API-8 fullscreen env(safe-area-inset-
        // bottom) reports 0, so also honor the SDK-published --tg-safe-bottom (device
        // home-indicator inset) — this is the fix for the nav clipping in fullscreen.
        paddingBottom: "calc(8px + max(env(safe-area-inset-bottom), var(--tg-safe-bottom, 0px)))",
        backdropFilter: "blur(12px)",
      }}
    >
      {TABS.map((tab) => {
        const on = active === tab.view;
        const badge = tab.view === "friends" ? friendsBadge : tab.view === "chat" ? chatBadge : 0;
        return (
          <button
            key={tab.view}
            type="button"
            aria-current={on ? "page" : undefined}
            onClick={() => onNavigate(tab.view)}
            className={`flex flex-col items-center justify-center gap-1 appearance-none border-0 cursor-pointer font-sans px-3 py-1 rounded-xl transition-all duration-200 active:scale-90 ${
              on
                ? "bg-secondary-container text-on-secondary-container"
                : "bg-transparent text-on-surface-variant hover:text-primary"
            }`}
          >
            <span className="relative flex">
              <NavIcon type={tab.icon} />
              {badge > 0 ? (
                <span
                  className="absolute -right-2 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-error px-1 text-[10px] font-bold leading-none text-on-error shadow"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {faBadge(badge)}
                </span>
              ) : null}
            </span>
            <span className="text-[11px] font-bold leading-none">{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
